import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useUIStore from '../../store/uiStore';
import * as expenseService from '../../services/expenseService';
import { LineChart } from '../../components/ExpenseCharts';
import ExpenseTabs from '../../components/ExpenseTabs';

/**
 * Deliberately the opposite of ExpenseDashboardPage: no filters, no
 * category/engineer breakdowns, no CSV export buttons - a CEO shouldn't
 * have to operate anything to see "are we OK this month". Four numbers,
 * one trend line, and a plain-English list of what needs attention.
 * Built per user request (2026-08-19) after comparing this module against
 * commercial expense tools - see conversation notes; those tools'
 * "executive dashboard" is consistently a separate, sparser screen from
 * the analyst view, not the same screen with fewer permissions.
 */
const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const KpiTile = ({ label, value, pct }) => {
  const hasTrend = pct !== null && pct !== undefined;
  const up = pct > 0;
  const flat = pct === 0;
  const color = flat ? 'text-white/60' : up ? 'text-red-200' : 'text-green-200';
  return (
    <div className="rounded-2xl p-6 border border-white/25 bg-white/10">
      <div className="text-xs text-white/70 mb-2">{label}</div>
      <div className="text-3xl font-medium text-white">{value}</div>
      {hasTrend && (
        <div className={`text-sm font-medium mt-2 ${color}`}>
          {flat ? '→' : up ? '↑' : '↓'} {Math.abs(pct)}% vs last month
        </div>
      )}
    </div>
  );
};

const ExpenseCeoSummaryPage = () => {
  const showToast = useUIStore((s) => s.showToast);
  const navigate = useNavigate();

  const [summary, setSummary] = useState(null);
  const [budget, setBudget] = useState([]);
  // Bug fixed 2026-08-19: this started as null, but nothingNeedsAttention
  // below is `alerts && ...` - before the API call resolved that evaluated
  // to null (falsy), so the ternary took the *else* branch and immediately
  // did alerts.over_budget_projects.map(...) on a null alerts, crashing the
  // whole page on every load before any data arrived. A safe empty default
  // means there's nothing to null-check.
  const [alerts, setAlerts] = useState({ pending_approvals: [], pending_approvals_count: 0, over_budget_projects: [], large_pending_vendor_payments: [] });
  const [monthlyTrend, setMonthlyTrend] = useState([]);

  useEffect(() => {
    Promise.all([
      expenseService.getDashboardSummary({ date_shortcut: 'this_month' }),
      expenseService.getDashboardBudget(),
      expenseService.getDashboardAlerts(),
      expenseService.getDashboardCharts({}),
    ])
      .then(([summaryRes, budgetRes, alertsRes, chartsRes]) => {
        setSummary(summaryRes.data.summary);
        setBudget(budgetRes.data.budget || []);
        setAlerts(alertsRes.data.alerts);
        setMonthlyTrend(chartsRes.data.charts?.monthly_trend || []);
      })
      .catch(() => showToast('Failed to load executive summary', 'error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const overBudgetCount = alerts?.over_budget_projects?.length || 0;
  const pendingCount = alerts?.pending_approvals_count || 0;
  const largePaymentsCount = alerts?.large_pending_vendor_payments?.length || 0;
  const nothingNeedsAttention = alerts && overBudgetCount === 0 && pendingCount === 0 && largePaymentsCount === 0;

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-4xl mx-auto">
        <ExpenseTabs />
        <h1 className="text-2xl font-bold text-white mb-1">Executive Summary</h1>
        <p className="text-sm text-white/60 mb-6">This month, at a glance.</p>

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
            <KpiTile label="Total this month" value={money(summary.total_expenses)} pct={summary.trend?.pct_change?.total_expenses} />
            <KpiTile label="Site" value={money(summary.site_expenses)} pct={summary.trend?.pct_change?.site_expenses} />
            <KpiTile label="Factory" value={money(summary.factory_expenses)} pct={summary.trend?.pct_change?.factory_expenses} />
            <KpiTile label="Vendor" value={money(summary.vendor_expenses)} pct={summary.trend?.pct_change?.vendor_expenses} />
            <KpiTile label="Material" value={money(summary.material_expenses)} pct={summary.trend?.pct_change?.material_expenses} />
            {/* Labour (production-tracker daily reports) added 2026-09-29
                per user request. */}
            <KpiTile label="Labour" value={money(summary.labour_cost)} pct={summary.trend?.pct_change?.labour_cost} />
          </div>
        )}

        <div className="glass-card p-6 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4">Needs attention</h2>
          {nothingNeedsAttention ? (
            <p className="text-sm text-gray-500">Nothing outstanding - no overdue approvals, no projects over budget, no large vendor payments pending.</p>
          ) : (
            <div className="space-y-2">
              {pendingCount > 0 && (
                <p className="text-sm text-gray-700">
                  <span className="font-medium">{pendingCount}</span> expense{pendingCount !== 1 ? 's' : ''} awaiting approval
                  {alerts.pending_approvals[0]?.days_pending > 0 && (
                    <span className="text-amber-700"> - oldest pending {alerts.pending_approvals[0].days_pending} day{alerts.pending_approvals[0].days_pending !== 1 ? 's' : ''}</span>
                  )}
                </p>
              )}
              {alerts.over_budget_projects.map((p) => (
                <p key={p.project_id} className="text-sm text-red-700">
                  <button onClick={() => navigate(`/expenses/site?project_id=${p.project_id}`)} className="font-medium hover:underline">{p.project_name}</button>
                  {' '}is {p.pct_used}% of budget ({money(p.actual)} of {money(p.budgeted)})
                </p>
              ))}
              {alerts.large_pending_vendor_payments.map((v) => (
                <p key={v.vendor_id} className="text-sm text-gray-700">
                  <button onClick={() => navigate(`/expenses/vendor?vendor_id=${v.vendor_id}`)} className="font-medium hover:underline">{v.vendor_name}</button>
                  {' '}is owed {money(v.amount_to_pay)}
                </p>
              ))}
            </div>
          )}
        </div>

        {budget.length > 0 && (
          <div className="glass-card p-6 mb-6">
            <h2 className="font-semibold text-gray-900 mb-4">Budget health</h2>
            <div className="space-y-3">
              {budget.map((b) => (
                <div key={b.project_id}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-700 font-medium">{b.project_name}</span>
                    <span className={b.over_budget ? 'text-red-600 font-medium' : 'text-gray-500'}>{b.pct_used}%</span>
                  </div>
                  <div className="bg-gray-100 rounded h-2">
                    <div
                      className={`h-2 rounded ${b.over_budget ? 'bg-red-500' : b.pct_used > 80 ? 'bg-amber-500' : 'bg-green-500'}`}
                      style={{ width: `${Math.min(b.pct_used, 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="glass-card p-6">
          <LineChart
            title="Monthly trend"
            data={monthlyTrend}
            xKey="month"
            series={[
              { key: 'site', color: '#2F6690', label: 'Site' },
              { key: 'factory', color: '#F59E0B', label: 'Factory' },
              { key: 'vendor', color: '#10B981', label: 'Vendor' },
              { key: 'material', color: '#8B5CF6', label: 'Material' },
              { key: 'labour', color: '#EC4899', label: 'Labour' },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default ExpenseCeoSummaryPage;
